import { Button, Col, Row, Select, Space } from "antd";
import { clientTabsConfig } from "config/tabsConfig";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LookupTable from "redux/models/lookupTable";
import { selectConfigration } from "redux/slices/configrationSlice";
import { selectUser, selectUserId } from "redux/slices/userSlice";
import K from "utilities/constants";
import ClientLookupTableModal from "./clientLookupTableModal";
import { usePageFilter } from "../../../../hooks/usePageFilters";
import { ParseString, useQueryState } from "../../../../hooks/useQueryState";
import { ClientSideDataGrid } from "../../../../common/components/clientSideDataGrid/clientSideDataGrid";
import {
  getClientColumns,
  getJobTitleColumns,
} from "../../../configurations/lookupTables/columns";
import AgGridUtility from "../../../../utilities/agGridUtility";
import { DataTableLayout } from "../../../../common/components/dataTableLayout/dataTableLayout";

export default function ClientLookupTablesContainer({ clientId }) {
  const gridRef = useRef();
  const dispatch = useDispatch();

  const userId = useSelector(selectUserId);
  const lookData = useSelector(selectConfigration).lookup;
  const userSlice = useSelector(selectUser);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editData, setEditData] = useState({});

  const [table, setTable] = usePageFilter(
    `clientLookUp_${clientId}_status`,
    K.LookupTabKeys.JobLocations,
  );

  const [tableParam, setTableParam] = useQueryState("table", table);

  const [search, setSearch] = usePageFilter(`clientLookUp_${clientId}_search`);

  const rowData = lookData[clientTabsConfig[tableParam]?.reduxKey]?.filter(
    (item) => Number(item.clientId) === Number(clientId),
  );

  const addNewRecord = () => {
    setEditData({});
    setIsModalVisible(true);
  };

  const editRecord = (params) => {
    setEditData(params.data);
    setIsModalVisible(true);
  };

  const columns = useMemo(
    () =>
      tableParam === K.LookupTabKeys.JobTitle
        ? getJobTitleColumns(userSlice.roles, editRecord)
        : getClientColumns(userSlice.roles, editRecord),
    [userSlice.roles, tableParam],
  );

  const onChange = (value) => {
    setTableParam(value);
    setTable(value);
  };

  useEffect(() => {
    const getLookupData = async (tab) => {
      try {
        await dispatch(
          LookupTable.getData(tab.reduxKey, `${tab.apiEndPoint}/${clientId}`),
        );
      } catch (error) {
        console.error(error);
      }
    };

    getLookupData(clientTabsConfig[tableParam]);
  }, [clientId, dispatch, tableParam]);

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Row justify="end">
        <Col style={{ width: 200 }}>
          <Select
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="label"
            onChange={onChange}
            defaultValue={tableParam}
            options={Object.keys(clientTabsConfig).map((key) => ({
              value: key,
              label: clientTabsConfig[key].name,
            }))}
          />
        </Col>
      </Row>

      <DataTableLayout
        searchDefaultValue={search}
        onSearchChange={setSearch}
        actions={[
          {
            permissions: [K.Permissions.CreateClientLookupTables],
            renderer: () => (
              <Col>
                <Button type="primary" onClick={addNewRecord}>
                  Add New
                </Button>
              </Col>
            ),
          },
        ]}
      >
        <ClientSideDataGrid
          key={tableParam}
          ref={gridRef}
          userId={userId}
          defaultColumnConfig={columns}
          configKey={
            tableParam === K.LookupTabKeys.JobTitle
              ? K.AgGridTable.Keys.ClientLookupJobTitleVisibleColumn
              : K.AgGridTable.Keys.ClientLookupVisibleColumn
          }
          rowData={rowData}
          quickFilterText={search}
          getContextMenuItems={() => AgGridUtility.menuItemsWithExport}
        />
      </DataTableLayout>

      <ClientLookupTableModal
        isVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        editData={editData}
        setEditData={setEditData}
        title={clientTabsConfig[tableParam]?.name}
        clientId={clientId}
        table={tableParam}
      />
    </Space>
  );
}
